import { useEffect, useState } from "react";
import 'chart.js/auto';
import { Chart } from 'react-chartjs-2';
import { GraphDataset, createGraphData, createGraphOption } from "../../../../../utils/chartUtils";
import { getRandomColors } from "../../../../../utils/colorUtils";
import { getDayName, getWeekName } from "../../../../../utils/dateFormatUtils";
import { toPriceUnitFormat } from "../../../../../utils/numberFormatUtils";
import useRouterHookV2 from "../../../../../hooks/router/useRouterHookV2";
import { CustomChartGroupV2 } from "../../../../../components/chart/v2/CustomChartGroup";

const SALES_GRAPH_BG_COLOR = ['#4CB0BD', '#AFF08B', '#9CB5D3', '#FFB5C5', '#F3CAF2', '#47CDBF', '#FFC8B4', '#FFDB86'];

export default function PayAmountDayOfWeekGraphComponent (props) {
    const [avgGraphData, setAvgGraphData] = useState(null);
    const [graphOption, setGraphOption] = useState(null);

    const router = useRouterHookV2();

    useEffect(() => {
        if(!router.state?.productCategoryIds) {
            handleActionResetGraphData();
            return;
        }

        if(!props.categories) {
            return;
        }

        if(!props.analysis) {
            return;
        }

        handleActionCreateAvgGraphData();
    }, [router.state, props.categories, props.analysis, props.checkedExcludedDeliveryChargeSwitch])

    useEffect(() => {
        if(!avgGraphData) {
            return;
        }

        handleActionCreateGraphOption();
    }, [avgGraphData])

    const handleActionResetGraphData = () => {
        setAvgGraphData(null);
    }

    const handleActionCreateAvgGraphData = () => {
        let salesPayAmountData = [];
        let salesDatasets = [];
        let graphLabels = getWeekName();
        let categoryIds = [...router.state.productCategoryIds];
        let selectedCategories = props.categories.filter(r => categoryIds.includes(r.id));
        
        let categoryPayAmount = selectedCategories.map(r => {
            let data = graphLabels.map(r2 => {
                return {
                    dayName: r2,
                    salesPayAmount: 0,
                    frequency: 0
                }
            })

            return {
                categoryId: r.id,
                categoryName: r.name,
                payAmountData: data
            }
        });

        /**
         * categoryPayAmount = [
         *  {
         *      category: A,
         *      payAmountData: [
         *          {dayName: '일', salesPayAmount: -, frequency: -},
         *          {dayName: '월', salesPayAmount: -. freqiemcy: -},
         *          ...
         *      ]
         *  }, {...}, {...}]
         */
        categoryPayAmount = categoryPayAmount.map(r => {
            let categoryPayAmountData = [...r.payAmountData];

            props.analysis.forEach(data => {
                let day = getDayName(data.datetime);

                data.performances.forEach(r2 => {
                    if (r2.productCategoryId === r.categoryId) {
                        let viewSalesPayAmount = props.checkedExcludedDeliveryChargeSwitch ? r2.excludedDeliveryChargeSalesPayAmount : r2.salesPayAmount;

                        categoryPayAmountData = categoryPayAmountData.map(r3 => {
                            if (r3.dayName === day) {
                                return {
                                    ...r3,
                                    salesPayAmount: r3.salesPayAmount + viewSalesPayAmount,
                                    frequency: r3.frequency + 1
                                }
                            } else {
                                return r3;
                            }
                        });
                    };
                });
            })

            return {
                ...r,
                payAmountData: categoryPayAmountData
            }
        });

        categoryPayAmount.forEach(r => {
            let categorySalesPayAmountData = [];
            r.payAmountData.forEach(r2 => {
                let salesAvg = parseInt((r2.salesPayAmount) / r2.frequency);
                categorySalesPayAmountData.push(salesAvg);
            })
            salesPayAmountData.push(categorySalesPayAmountData);
        })

        let randomColor = getRandomColors(selectedCategories.length - SALES_GRAPH_BG_COLOR.length)
        let graphColor = [...SALES_GRAPH_BG_COLOR, ...randomColor];

        if (selectedCategories.size === 0) {
            let barGraphOfSales = {
                ...new GraphDataset().toJSON(),
                type: 'bar',
                label: '판매 매출액',
                data: [],
                borderColor: graphColor[0],
                backgroundColor: graphColor[0],
                borderWidth: 0,
                order: 0
            }
            salesDatasets.push(barGraphOfSales);
        } else {
            categoryPayAmount.forEach((r, idx) => {
                let categoryName = (!r.categoryName || r.categoryName === '') ? '미지정' : r.categoryName;

                let barGraphOfSales = {
                    ...new GraphDataset().toJSON(),
                    type: 'bar',
                    label: categoryName,
                    data: salesPayAmountData[idx],
                    borderColor: graphColor[idx],
                    backgroundColor: graphColor[idx],
                    borderWidth: 0,
                    order: 0
                }
                salesDatasets.push(barGraphOfSales);
            })
        }

        // 매출 그래프 데이터 생성
        setAvgGraphData({
            labels: graphLabels,
            datasets: salesDatasets
        });
    }

    const handleActionCreateGraphOption = () => {
        let option = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            scales: {
                y: {
                    ticks: {
                        callback: function (value, index, ticks) {
                            return toPriceUnitFormat(value);
                        }
                    }
                },
                x: {
                    grid: {
                        display: false
                    }
                }
            }
        }

        setGraphOption(option);
    }

    return (
        <CustomChartGroupV2>
            <CustomChartGroupV2.BoardGroup>
                <CustomChartGroupV2.BoardGroup.Title>요일별 매출액</CustomChartGroupV2.BoardGroup.Title>
                <CustomChartGroupV2.BoardGroup.Info>조회된 기간의 요일별 평균 판매액이 표시됩니다.</CustomChartGroupV2.BoardGroup.Info>
            </CustomChartGroupV2.BoardGroup>

            <CustomChartGroupV2.BodyGroup>
                <CustomChartGroupV2.BodyGroup.ChartBody>
                    {avgGraphData && graphOption &&
                        <Chart
                            data={createGraphData(avgGraphData)}
                            options={createGraphOption(graphOption)}
                        />
                    }
                </CustomChartGroupV2.BodyGroup.ChartBody>
            </CustomChartGroupV2.BodyGroup>
        </CustomChartGroupV2>
    )
}